'use client'
import { type DialogHTMLAttributes, useEffect, useRef } from 'react'

import { closeOnBackdropClick, useDialogPolyfill } from './hooks'

export type ModalProps = Pick<
	DialogHTMLAttributes<HTMLDialogElement>,
	'aria-labelledby' | 'children' | 'className' | 'open' | 'style'
> & {
	closeOnBackdrop?: boolean
	onRequestClose: () => unknown
	rootClassName?: string
}

export function ControlledDialog({
	children,
	className,
	closeOnBackdrop = true,
	onRequestClose,
	open = false,
	rootClassName,
	...props
}: ModalProps) {
	const dialogRef = useRef<HTMLDialogElement>(null)
	useDialogPolyfill(dialogRef)

	/**
	 * @hint prevents calling imperative methods on mount since the polyfill will throw
	 * an error since we are not using the `open` attribute
	 */
	useEffect(() => {
		const dialogNode = dialogRef.current

		open ? dialogNode?.showModal() : dialogNode?.close()
	}, [open])

	return (
		<dialog
			className={rootClassName}
			onCancel={event => {
				if (!closeOnBackdrop) {
					event.preventDefault()
				}
			}}
			onClick={closeOnBackdrop ? closeOnBackdropClick : undefined}
			onClose={onRequestClose}
			ref={dialogRef}
			{...props}
		>
			<div className={className}>{children}</div>
		</dialog>
	)
}
